import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Pause, Play, Upload as UploadIcon, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Alert } from "@/components/ui/Feedback";
import {
  CHUNK_SIZE,
  compressionFor,
  isDumpFile,
  rememberUpload,
  resumableUpload,
  sendFile,
  type UploadProgress,
} from "@/lib/chunked-upload";
import { cn } from "@/lib/cn";
import { formatBytes, formatDateTime, formatDuration } from "@/lib/format";
import { importsApi, type UploadInfo } from "@/lib/imports-api";

import { importErrorText } from "./errors";
import { ProgressBar } from "./ProgressBar";

type Phase =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "paused" }
  | { kind: "done"; fileName: string }
  | { kind: "error"; message: string };

function percent(done: number, total: number): string {
  return `${Math.floor((done / Math.max(1, total)) * 1000) / 10}%`;
}

export function UploadCard() {
  const { t } = useTranslation();
  const inputId = useId();
  const queryClient = useQueryClient();
  const uploads = useQuery({ queryKey: ["imports", "uploads"], queryFn: importsApi.uploads });
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const controller = useRef<AbortController | null>(null);
  const active = useRef<UploadInfo | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const unfinished = uploads.data?.filter((u) => u.status === "uploading") ?? [];
  const resumable = file && uploads.data ? resumableUpload(file, uploads.data) : null;
  const busy = phase.kind === "sending";

  // Leaving the page stops the upload: ask first. Unmounting pauses it.
  useEffect(() => {
    if (!busy) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [busy]);
  useEffect(() => () => controller.current?.abort(), []);

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    active.current = null;
    setProgress(null);
    if (chosen && !isDumpFile(chosen.name)) {
      setFile(null);
      setPhase({ kind: "error", message: t("imports.errors.unsupported_file") });
      return;
    }
    setFile(chosen);
    setPhase({ kind: "idle" });
  };

  const send = async () => {
    if (!file) return;
    const abort = new AbortController();
    controller.current = abort;
    setPhase({ kind: "sending" });
    try {
      let upload = active.current ?? resumable;
      if (upload) {
        // Continue from what the server actually holds.
        upload =
          (await importsApi.uploads()).find((u) => u.id === upload?.id && u.status === "uploading") ?? null;
      }
      if (!upload) {
        upload = await importsApi.startUpload({
          original_name: file.name,
          size: file.size,
          chunk_size: CHUNK_SIZE,
          compression: compressionFor(file),
        });
        rememberUpload(upload, file);
      }
      active.current = upload;
      const done = await sendFile(file, upload, { signal: abort.signal, onProgress: setProgress });
      active.current = null;
      setFile(null);
      if (input.current) input.current.value = "";
      setProgress(null);
      setPhase({ kind: "done", fileName: done.file_name });
    } catch (error) {
      if (abort.signal.aborted) setPhase({ kind: "paused" });
      else setPhase({ kind: "error", message: importErrorText(error, t) });
    } finally {
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
    }
  };

  const cancel = async (upload: UploadInfo | null) => {
    controller.current?.abort();
    if (upload) await importsApi.cancelUpload(upload.id).catch(() => undefined);
    if (!upload || upload.id === active.current?.id) {
      active.current = null;
      setProgress(null);
      setPhase({ kind: "idle" });
    }
    void queryClient.invalidateQueries({ queryKey: ["imports"] });
  };

  const started = progress !== null && (busy || phase.kind === "paused");

  return (
    <Card aria-labelledby="upload-title">
      <CardHeader title={<span id="upload-title">{t("imports.upload.title")}</span>} />
      <CardBody className="space-y-4">
        <p className="text-sm text-ink-muted">{t("imports.upload.intro")}</p>

        <div className="space-y-1.5">
          <p id={`${inputId}-label`} className="text-sm font-medium text-ink">
            {t("imports.upload.file")}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {/* The native control is hidden: its button text follows the browser's language, not the app's. */}
            <input
              ref={input}
              id={inputId}
              type="file"
              accept=".sql,.gz"
              disabled={busy}
              onChange={choose}
              aria-labelledby={`${inputId}-label`}
              className="peer sr-only"
            />
            <label
              htmlFor={inputId}
              className={cn(
                "inline-flex h-control cursor-pointer items-center gap-2 rounded-control border border-line-strong",
                "bg-surface px-3 text-sm font-medium text-ink hover:bg-sunken",
                "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--tv-focus)]",
                "peer-disabled:cursor-not-allowed peer-disabled:opacity-60",
              )}
            >
              <FileUp className="size-4" aria-hidden="true" />
              {t("imports.upload.choose")}
            </label>
            <span className="min-w-0 text-sm break-all text-ink-muted">
              {file ? file.name : t("imports.upload.noFile")}
            </span>
          </div>
          {file && (
            <p className="text-sm text-ink-muted">
              {formatBytes(file.size)}
              {" · "}
              {compressionFor(file) === "gzip" ? t("imports.upload.willCompress") : t("imports.upload.asIs")}
            </p>
          )}
        </div>

        {resumable && !started && (
          <Alert tone="accent">
            {t("imports.upload.resumeFound", {
              percent: percent(resumable.received_bytes, resumable.source_size),
            })}
          </Alert>
        )}

        {started && progress && (
          <div className="space-y-2">
            <ProgressBar
              value={progress.sentBytes / progress.totalBytes}
              label={t("imports.upload.progressLabel")}
            />
            <p className="text-sm text-ink-muted tabular-nums" aria-live="off">
              {t("imports.upload.progress", {
                sent: formatBytes(progress.sentBytes),
                total: formatBytes(progress.totalBytes),
                percent: percent(progress.sentBytes, progress.totalBytes),
              })}
              {progress.bytesPerSecond !== null && busy && (
                <>
                  {" · "}
                  {t("imports.upload.speed", { speed: formatBytes(progress.bytesPerSecond) })}
                  {progress.secondsLeft !== null &&
                    ` · ${t("imports.upload.left", { time: formatDuration(progress.secondsLeft) })}`}
                </>
              )}
              {progress.storedBytes > 0 && progress.storedBytes < progress.sentBytes && (
                <>
                  {" · "}
                  {t("imports.upload.compressed", { stored: formatBytes(progress.storedBytes) })}
                </>
              )}
            </p>
          </div>
        )}

        {phase.kind === "paused" && <Alert tone="accent">{t("imports.upload.paused")}</Alert>}
        {phase.kind === "error" && <Alert>{phase.message}</Alert>}
        {phase.kind === "done" && (
          <Alert tone="primary">{t("imports.upload.done", { file: phase.fileName })}</Alert>
        )}

        <div className="flex flex-wrap gap-2">
          {busy ? (
            <Button variant="secondary" onClick={() => controller.current?.abort()}>
              <Pause className="size-4" aria-hidden="true" />
              {t("imports.upload.pause")}
            </Button>
          ) : (
            <Button onClick={() => void send()} disabled={!file}>
              {started || resumable ? (
                <Play className="size-4" aria-hidden="true" />
              ) : (
                <UploadIcon className="size-4" aria-hidden="true" />
              )}
              {started || resumable ? t("imports.upload.resume") : t("imports.upload.start")}
            </Button>
          )}
          {started && (
            <Button variant="danger" onClick={() => void cancel(active.current)}>
              <X className="size-4" aria-hidden="true" />
              {t("imports.upload.cancel")}
            </Button>
          )}
        </div>

        {unfinished.length > 0 && !started && (
          <div className="space-y-2 border-t border-line pt-4">
            <h3 className="text-sm font-medium">{t("imports.upload.unfinished")}</h3>
            <p className="text-xs text-ink-subtle">{t("imports.upload.unfinishedHint")}</p>
            <ul className="divide-y divide-line">
              {unfinished.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span className="min-w-0 break-all">
                    <span className="font-medium">{u.original_name}</span>{" "}
                    <span className="text-ink-muted tabular-nums">
                      {percent(u.received_bytes, u.source_size)} · {formatDateTime(u.updated_at)}
                    </span>
                  </span>
                  <Button variant="ghost" onClick={() => void cancel(u)}>
                    {t("imports.upload.discard")}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
